import Button from "common/components/Button/Button";

import { Specy } from "types/Specy";
import { Link } from "react-router-dom";
import useAuth from "common/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { TableCellsIcon } from "@heroicons/react/24/solid";
import AnimalListOverview from "components/Item/AnimalListOverview";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import { ObservationGrid } from "types/ObservationGrid";
import { useMemo } from "react";
import ObservationGroupListOverview from "components/Item/ObservationGroupListOverview";

function SpecyItem({ item, onDelete, onNavigate }: any) {
  const { user: currentUser } = useAuth();
  const canSeeDetailsFromItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.species?.read)) ??
    false;
  const canEditItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.species?.update)) ??
    false;
  const canDeleteItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.species?.delete)) ??
    false;

  const {
    name,
    scientificName,
    englishName,
    animals,
    observationGrids,
    observationGroups,
  } = item as Specy;

  const editLink = `/species/edit/${item.id}`;

  const { t } = useTranslation();

  const observationGroupsAnimals = useMemo(() => {
    return Array.from(
      new Map(
        observationGroups
          .flatMap((outer) => outer.animals)
          .map((animal) => [animal?.id, animal])
      ).values()
    );
  }, [observationGroups]);

  return (
    <div className="flex px-4 py-4 mt-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all">
      <Link
        to={editLink}
        className={
          !canSeeDetailsFromItem
            ? "pointer-events-none flex flex-1"
            : "flex flex-1"
        }
      >
        <div className="flex flex-col flex-1 justify-between pl-4 pr-6">
          <div className="flex mt-4 mb-2">
            <div className="flex flex-1 gap-3">
              <h4 className="text-xl font-bold text-primary leading-4">
                {scientificName}
              </h4>
              <span className="font-bold">{name}</span>
              <span className="font-semibold">{englishName}</span>
            </div>
            {Boolean(animals?.length) && (
              <AnimalListOverview animals={animals} />
            )}
            {Boolean(observationGroupsAnimals?.length) && (
              <ObservationGroupListOverview
                observationGroups={observationGroups}
                title={t("animals.animalsInGroup")}
              />
            )}
          </div>
        </div>
      </Link>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 lg:min-w-[22em] border-slate-150 gap-2">
        <TooltipWrapper
          className="mr-1"
          content={
            observationGrids?.length > 0 ? (
              <div className="flex flex-col flex-wrap gap-1">
                {observationGrids?.map((grid: ObservationGrid) => (
                  <span className="font-bold">{grid.name}</span>
                ))}
              </div>
            ) : null
          }
        >
          <div className="flex items-center gap-1 text-sm">
            <TableCellsIcon className="w-3 h-3" />
            <span className="font-bold">
              {t("forms.observationGridsLength", {
                count: observationGrids?.length || 0,
              })}
            </span>
          </div>
        </TooltipWrapper>
        <div className="flex w-full items-center justify-end text-slate-500 gap-1">
          <TooltipWrapper content={<span>{t("actions.delete")}</span>}>
            <Button
              type="error-line"
              icon="TrashIcon"
              compact
              onClick={() => onDelete(item.id)}
              disabled={!canDeleteItem}
              confirmMessage={t("actions.confirmDelete")}
            />
          </TooltipWrapper>

          <Button
            title={t("actions.edit")}
            type="warning-line"
            icon="PencilSquareIcon"
            onClick={() => onNavigate(item.id)}
            disabled={!canEditItem}
          />
        </div>
      </div>
    </div>
  );
}

export default SpecyItem;
